import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { Tag, Clock, Search } from "lucide-react";
import { formatTimeAgo } from "./utils";

const HighlightText = ({ text, query }) => {
  if (!query || !text) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark
            key={i}
            style={{
              backgroundColor: "#ECB236",
              color: "#000",
              fontWeight: 700,
              borderRadius: "3px",
              padding: "0 2px",
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

export const SearchResults = ({ results, query, loading }) => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";
  const navigate = useNavigate();

  const getExcerpt = (content, searchQuery) => {
    if (!content) return "";
    const maxLength = 120;
    const lowerContent = content.toLowerCase();
    const lowerQuery = searchQuery.toLowerCase();
    const index = lowerContent.indexOf(lowerQuery);

    let start = 0;
    if (index !== -1) {
      start = Math.max(0, index - 40);
    }
    let excerpt = content.substring(start, start + maxLength);
    if (start > 0) excerpt = "…" + excerpt;
    if (start + maxLength < content.length) excerpt = excerpt + "…";
    return excerpt;
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
        <Search size={22} style={{ color: theme.colors.primary }} />
        <h2
          style={{
            color: theme.colors.textColor || theme.colors.textColorNavbar,
            margin: 0,
            fontSize: "1.6rem",
            fontWeight: 700,
          }}
        >
          {loading
            ? `Buscando "${query}"...`
            : `${results.length} resultado${results.length !== 1 ? "s" : ""} para "${query}"`}
        </h2>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: isDark ? "#64748b" : "#94a3b8" }}>
          Buscando publicaciones...
        </div>
      ) : results.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: isDark ? "#64748b" : "#94a3b8",
            backgroundColor: isDark ? "#141528" : "#f8fafc",
            borderRadius: "16px",
            border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
          }}
        >
          <Search size={48} style={{ marginBottom: "16px", opacity: 0.5 }} />
          <p style={{ fontSize: "var(--font-size-body, 16px)", fontWeight: 600, margin: 0 }}>
            No se encontraron resultados
          </p>
          <p style={{ fontSize: "var(--font-size-min, 15px)", margin: "8px 0 0 0" }}>
            Intenta con otros términos de búsqueda
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {results.map((post) => (
            <div
              key={post.id}
              onClick={() => navigate(`/foro/discussion/${post.discussion_id}`)}
              style={{
                backgroundColor: isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff"),
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "12px",
                padding: "20px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
                e.currentTarget.style.borderColor = "#ECB236";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
              }}
            >
              {/* Discussion title */}
              <h3
                style={{
                  color: theme.colors.textColor || theme.colors.textColorNavbar,
                  margin: "0 0 12px 0",
                  fontSize: "var(--font-size-md, 18px)",
                  fontWeight: 700,
                  lineHeight: 1.3,
                }}
              >
                <HighlightText text={post.discussion_title} query={query} />
              </h3>

              {/* Post excerpt (only if content matched) */}
              {post.matched_content && (
                <p
                  style={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontSize: "var(--font-size-body, 16px)",
                    lineHeight: 1.5,
                    margin: "0 0 16px 0",
                  }}
                >
                  <HighlightText text={getExcerpt(post.content, query)} query={query} />
                </p>
              )}

              {/* Meta row */}
              <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                {/* Author */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <img
                    src={post.author_picture || `https://udalba.tipddy.cl/theme/image.php/boost/core/1/u/f1`}
                    alt={post.author_name}
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                  <span style={{ color: theme.colors.textColor || theme.colors.textColorNavbar, fontSize: "var(--font-size-min, 15px)", fontWeight: 600 }}>
                    {post.author_name}
                  </span>
                </div>

                {/* Category */}
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Tag size={14} style={{ color: theme.colors.primary }} />
                  <span style={{ color: theme.colors.primary, fontSize: "var(--font-size-min, 15px)", fontWeight: 600 }}>
                    {post.category_name}
                  </span>
                </div>

                {/* Date */}
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Clock size={14} style={{ color: isDark ? "#64748b" : "#94a3b8" }} />
                  <span style={{ color: isDark ? "#64748b" : "#94a3b8", fontSize: "var(--font-size-min, 15px)" }}>
                    {formatTimeAgo(post.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
